import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Chip, CircularProgress, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

import useReleveHeuresStyles from './ReleveHeures.styles';
import WeekHeader from './WeekHeader';
import DayCard from './DayCard';
import RecapitulatifCard from './RecapitulatifCard';
import ValidationCard from './ValidationCard';
import TimePickerDialog from './TimePickerDialog';
import BottomActionBar from './BottomActionBar';
import ConfirmSwitchWeekDialog from './ConfirmSwitchWeekDialog';
import NotificationSnackbar from './NotificationSnackbar';
import { exporterReleveHeuresPDF } from './exportPdf';
import { JOURS, timeToMinutes, depBrutHeures, calculerTotalJour, calculerTotalSemaineNet, pauseInsuffisante, getWeekBoundaries } from '../../../utils/releveHeures';
import {
    chargerReleveHeures, soumettreReleveHeures,
    setReleveHeuresSemaine, setReleveHeuresExpandedDay, setReleveHeuresChamp,
    setReleveHeuresMeta, appliquerJourneeType, appliquerSemaineType
} from '../../../actions/releveHeures';

// Pour le moment, le relevé d'heures est toujours affiché/enregistré sous cet utilisateur, quel que soit le compte connecté
const ID_USER_AUTORISE = 215;

const focusElementById = (id, delay = 100) => {
    setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.focus();
    }, delay);
};

export default function ReleveHeures() {
    const classes = useReleveHeuresStyles();
    const dispatch = useDispatch();
    const userId = ID_USER_AUTORISE;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

    const weekString = useSelector(state => state.releveHeures.weekString);
    const statut = useSelector(state => state.releveHeures.statut);
    const expandedDay = useSelector(state => state.releveHeures.expandedDay);
    const details = useSelector(state => state.releveHeures.details);
    const meta = useSelector(state => state.releveHeures.meta);
    const saving = useSelector(state => state.releveHeures.saving);
    const loading = useSelector(state => state.releveHeures.loading);

    const { year: annee, week: semaine, monday, sunday } = getWeekBoundaries(weekString);
    const isLocked = statut === 'VALIDE';
    // Formulaire en lecture seule si validé en base, mais aussi tant que le service de vérification n'a pas répondu
    // (au lancement et à chaque changement de semaine) : impossible d'éditer une semaine déjà VALIDE côté serveur
    const isReadOnly = isLocked || loading;

    const [timePicker, setTimePicker] = useState({ open: false, jour: '', field: '', h: '08', m: '00', mode: 'hours' });
    const [pendingWeek, setPendingWeek] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const showSnackbar = useCallback((message, severity = 'info') => {
        setSnackbar({ open: true, message, severity });
    }, []);
    const closeSnackbar = useCallback(() => setSnackbar(prev => ({ ...prev, open: false })), []);

    useEffect(() => {
        dispatch(chargerReleveHeures(userId, annee, semaine));
    }, [dispatch, userId, annee, semaine]);

    const { totalSemaine, totalSemaineBrut, totalDeplacementsBruts, totauxParJour } = useMemo(() => {
        let grandTotalBrut = 0;
        let grandTotalDep = 0;
        const totauxParJour = {};

        JOURS.forEach(jour => {
            const jourKey = jour.toLowerCase();
            const d = details[jourKey];
            const totalJour = calculerTotalJour(d);
            totauxParJour[jourKey] = totalJour;
            grandTotalBrut += totalJour;
            grandTotalDep += depBrutHeures(d?.depBrut);
        });

        return {
            totalSemaineBrut: parseFloat(grandTotalBrut.toFixed(2)),
            totalSemaine: calculerTotalSemaineNet(grandTotalBrut),
            totalDeplacementsBruts: parseFloat(grandTotalDep.toFixed(2)),
            totauxParJour
        };
    }, [details]);

    const heuresSup = Math.max(0, totalSemaine - 39).toFixed(2);

    // Formulaire considéré comme rempli dès qu'un jour a un début/fin/déplacement/observation renseigné
    const hasFormData = useMemo(() => JOURS.some(jour => {
        const d = details[jour.toLowerCase()];
        return !!(d && (d.debut || d.fin || d.depBrut || (d.obs && d.obs.trim())));
    }), [details]);

    const handleChange = useCallback((jour, field, value) => {
        if (isReadOnly) return;
        dispatch(setReleveHeuresChamp(jour, field, value));
    }, [dispatch, isReadOnly]);

    const handlePauseChange = (jour, value) => {
        if (pauseInsuffisante(details[jour], value)) {
            showSnackbar('Une pause de 30 minutes minimum est obligatoire au-delà de 6h de travail continu.', 'warning');
            return;
        }
        handleChange(jour, 'pause', value);
        focusElementById(`dep-${jour}`);
    };

    const handleDepChange = (jour, value) => {
        handleChange(jour, 'depBrut', value);
        focusElementById(`obs-${jour}`);
    };

    const openClockPicker = (jour, field, currentVal) => {
        if (isReadOnly) return;
        let h = '08';
        let m = '00';
        if (currentVal && currentVal.includes(':')) {
            const [hh, mm] = currentVal.split(':');
            h = hh.padStart(2, '0');
            m = mm.padStart(2, '0');
        }
        setTimePicker({ open: true, jour, field, h, m, mode: 'hours' });
    };

    // Valide les observations du jour : passe au début du jour suivant, ou à la signature si Samedi
    const handleValiderObservation = (jour) => {
        const currentIndex = JOURS.findIndex(j => j.toLowerCase() === jour);

        if (currentIndex !== -1 && currentIndex < JOURS.length - 1) {
            const nextJour = JOURS[currentIndex + 1].toLowerCase();
            dispatch(setReleveHeuresExpandedDay(nextJour));
            setTimeout(() => openClockPicker(nextJour, 'debut', details[nextJour]?.debut || ''), 250);
        } else {
            dispatch(setReleveHeuresExpandedDay(''));
            setTimeout(() => {
                const sigEl = document.getElementById('signature-field');
                if (sigEl) {
                    sigEl.focus();
                    sigEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 150);
        }
    };

    const handleMinuteClick = (selectedM) => {
        const currentH = timePicker.h;
        const formatted = `${currentH.padStart(2, '0')}:${selectedM.padStart(2, '0')}`;
        const targetJour = timePicker.jour;
        const jourData = details[targetJour];

        if (timePicker.field === 'fin' && jourData.debut) {
            if (timeToMinutes(formatted) <= timeToMinutes(jourData.debut)) {
                showSnackbar(`L'heure de fin (${formatted}) doit être strictement supérieure à l'heure de début (${jourData.debut}).`, 'warning');
                return;
            }
        }

        handleChange(targetJour, timePicker.field, formatted);

        if (timePicker.field === 'debut') {
            const finVal = jourData.fin || '';
            const [h, m] = finVal.includes(':')
                ? finVal.split(':')
                : [String(Math.min(23, Number(currentH) + 8)).padStart(2, '0'), selectedM];

            setTimePicker({
                open: true,
                jour: targetJour,
                field: 'fin',
                h: h.padStart(2, '0'),
                m: m.padStart(2, '0'),
                mode: 'hours'
            });
        } else if (timePicker.field === 'fin') {
            if (pauseInsuffisante({ debut: jourData.debut, fin: formatted }, jourData.pause)) {
                handleChange(targetJour, 'pause', '00:30');
                showSnackbar('Une pause de 30 minutes minimum a été appliquée automatiquement : elle est obligatoire au-delà de 6h de travail continu.', 'info');
            }
            setTimePicker(prev => ({ ...prev, open: false }));
            focusElementById(`pause-${targetJour}`, 150);
        }
    };

    const handleSoumettre = async (action) => {
        const res = await dispatch(soumettreReleveHeures({ userId, annee, semaine, totalSemaine, details, meta, action }));
        if (res.success) {
            showSnackbar(res.message || (action === 'BROUILLON' ? 'Brouillon sauvegardé !' : 'Validé avec succès !'), 'success');
            dispatch(chargerReleveHeures(userId, annee, semaine));
        } else {
            showSnackbar('Erreur : ' + (res.error || "Impossible d'enregistrer"), 'error');
        }
        return res;
    };

    // Changement de semaine : si la semaine en cours n'est pas validée et contient des données, on demande confirmation
    const handleChangeWeek = (newWeekString) => {
        if (!newWeekString || newWeekString === weekString) return;
        if (!isLocked && hasFormData) {
            setPendingWeek(newWeekString);
        } else {
            dispatch(setReleveHeuresSemaine(newWeekString));
        }
    };

    const handleCancelWeekChange = () => setPendingWeek(null);

    const handleDiscardAndChangeWeek = () => {
        dispatch(setReleveHeuresSemaine(pendingWeek));
        setPendingWeek(null);
    };

    const handleSaveDraftAndChangeWeek = async () => {
        // Évite de rappeler handleSoumettre (qui recharge l'ancienne semaine) juste avant de changer de semaine :
        // sinon la requête de rechargement peut aboutir après celle de la nouvelle semaine et écraser son affichage
        const res = await dispatch(soumettreReleveHeures({ userId, annee, semaine, totalSemaine, details, meta, action: 'BROUILLON' }));
        if (res.success) {
            showSnackbar(res.message || 'Brouillon sauvegardé !', 'success');
            dispatch(setReleveHeuresSemaine(pendingWeek));
            setPendingWeek(null);
        } else {
            showSnackbar('Erreur : ' + (res.error || "Impossible d'enregistrer"), 'error');
        }
    };

    const handleExportPDF = async () => {
        await exporterReleveHeuresPDF({
            monday, sunday, details, totalSemaine, totalSemaineBrut, totalDeplacementsBruts, heuresSup,
            autorisationDir: meta.autorisationDir, reposRespectes: meta.reposRespectes,
            remarquesRepos: meta.remarquesRepos, signatureName: meta.signatureName, semaine, annee,
            dateSignature: meta.dateSignature, adresseIp: meta.adresseIp, empreinteSha256: meta.empreinteSha256
        });
    };

    return (
        <div className={classes.root}>
            <Container maxWidth="md">
                <WeekHeader
                    classes={classes}
                    isMobile={isMobile}
                    weekString={weekString}
                    onChangeWeek={handleChangeWeek}
                    monday={monday}
                    sunday={sunday}
                    isLocked={isReadOnly}
                    totalSemaine={totalSemaine}
                    onAppliquerSemaineType={() => dispatch(appliquerSemaineType())}
                    statutLabel={
                        loading ? (
                            <Chip label="Vérification..." icon={<CircularProgress size={16} style={{ marginLeft: 8 }} />} style={{ fontWeight: 'bold' }} />
                        ) : (
                            <Chip
                                label={isLocked ? 'Validé & Verrouillé' : 'Brouillon'}
                                color={isLocked ? 'secondary' : 'default'}
                                icon={isLocked ? <CheckCircleIcon /> : null}
                                style={{ fontWeight: 'bold' }}
                            />
                        )
                    }
                />

                {JOURS.map(jour => {
                    const jourKey = jour.toLowerCase();
                    return (
                        <DayCard
                            key={jour}
                            classes={classes}
                            isMobile={isMobile}
                            jour={jour}
                            data={details[jourKey]}
                            totalJour={totauxParJour[jourKey]}
                            isLocked={isReadOnly}
                            isExpanded={expandedDay === jourKey || isLocked}
                            onToggleExpand={() => dispatch(setReleveHeuresExpandedDay(expandedDay === jourKey ? '' : jourKey))}
                            onApplyJourneeType={() => dispatch(appliquerJourneeType(jourKey))}
                            onOpenClockPicker={(field) => openClockPicker(jourKey, field, details[jourKey][field])}
                            onPauseChange={(value) => handlePauseChange(jourKey, value)}
                            onDepChange={(value) => handleDepChange(jourKey, value)}
                            onObsChange={(value) => handleChange(jourKey, 'obs', value)}
                            onValiderObservation={() => handleValiderObservation(jourKey)}
                        />
                    );
                })}

                <RecapitulatifCard
                    classes={classes}
                    isMobile={isMobile}
                    totalSemaine={totalSemaine}
                    totalSemaineBrut={totalSemaineBrut}
                    totalDeplacementsBruts={totalDeplacementsBruts}
                    heuresSup={heuresSup}
                />

                <ValidationCard
                    classes={classes}
                    isMobile={isMobile}
                    isLocked={isReadOnly}
                    heuresSup={heuresSup}
                    meta={meta}
                    onChangeMeta={(patch) => dispatch(setReleveHeuresMeta(patch))}
                />
            </Container>

            <TimePickerDialog
                classes={classes}
                timePicker={timePicker}
                details={details}
                onClose={() => setTimePicker(p => ({ ...p, open: false }))}
                onSelectMode={(mode) => setTimePicker(p => ({ ...p, mode }))}
                onSelectHour={(h) => setTimePicker(p => ({ ...p, h, mode: 'minutes' }))}
                onSelectMinute={handleMinuteClick}
            />

            <BottomActionBar
                classes={classes}
                isLocked={isLocked}
                disabled={loading}
                signatureName={meta.signatureName}
                onSaveDraft={() => handleSoumettre('BROUILLON')}
                onValidate={() => handleSoumettre('VALIDE')}
                onExportPDF={handleExportPDF}
            />

            <ConfirmSwitchWeekDialog
                open={!!pendingWeek}
                saving={saving}
                onCancel={handleCancelWeekChange}
                onDiscard={handleDiscardAndChangeWeek}
                onSaveAndSwitch={handleSaveDraftAndChangeWeek}
            />

            <NotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={closeSnackbar}
            />
        </div>
    );
}
